import React from 'react';
import { connect } from 'react-redux';
import {
  OneThreeArticleList,
  HeadlinesArticleList,
  ScrollableArticleList,
  Section,
  SectionHeader,
  AppContent,
  ErrorBoundary, Ad,
} from 'mm-ui-components';
import { Store } from 'redux';
import { Theme } from '../../components/connectedComponents/Theme';
import { MetaTags } from '../../components/connectedComponents/MetaTags';
import { HeadScripts } from '../../components/connectedComponents/HeadScripts';
import { SECTION_NAMES, SLOT_NAMES } from '../templates.const';
import { Page } from '../Page';
import {
    getSectionArticlesByKey,
    getSectionDisplayNameByKey,
    getSectionLinkByKey,
    getSectionTextByKey,
} from '../../store/template/homePage/homepage.selectors';
import { HeaderConnected } from '../../components/connectedComponents/HeaderConnected';
import { FooterConnected } from '../../components/connectedComponents/FooterConnected';
import { MainWrapperConnected } from '../../components/connectedComponents/MainWrapperConnected';
import { getAdSlotId } from '../../store/config/config.selectors';
import { Article } from '../../store/template/template.state.types';
import { ErrorReporter } from '../../errorReporting/errorReporter.type';
import { AppActions, AppState, HomePageAppState } from '../../store/initialStateRegistration';
import { useMeasureHydrateTime } from '../useMeasureHydrateTime';

interface HomePageProps {
    store: Store<AppState, AppActions>
    errorProvider: ErrorReporter
}

interface HomePageContentProps {
    topSectionDisplayName: string,
    topSectionArticles: Array<Article>,
    cardsSectionDisplayName: string,
    cardsSectionArticles: Array<Article>,
    cardsSectionLink: string | null,
    cardsSectionText: string | null,
    contentSection1DisplayName: string,
    contentSection1Articles: Array<Article>,
    contentSection1Link: string | null,
    contentSection1Text: string | null,
    contentSection2DisplayName: string,
    contentSection2Articles: Array<Article>,
    contentSection2Link: string | null,
    contentSection2Text: string | null,
    contentSection3DisplayName: string,
    contentSection3Articles: Array<Article>,
    contentSection3Link: string | null,
    contentSection3Text: string | null,
    articles: Array<Article>
    topAdId: string | null,
    oneOnOneId: string | null
}

const getArticlesInTemplate = (state:AppState) => {
    const topSectionArticles = getSectionArticlesByKey('topSection')(state) || [];
    const cardsSectionArticles = getSectionArticlesByKey('cardsSection')(state) || [];
    const contentSection1Articles = getSectionArticlesByKey('contentSection1')(state) || [];
    const contentSection2Articles = getSectionArticlesByKey('contentSection2')(state) || [];
    const contentSection3Articles = getSectionArticlesByKey('contentSection3')(state) || [];
    return [
                ...topSectionArticles,
                ...cardsSectionArticles,
                ...contentSection1Articles,
                ...contentSection2Articles,
                ...contentSection3Articles,
            ];
};

export const HomePageComponent: React.FunctionComponent<HomePageProps> = ({ store, errorProvider }) => {
    useMeasureHydrateTime();
    return (
      <Page store={store} errorProvider={errorProvider}>
        <ErrorBoundary>
          <HomePageContentConnected />
        </ErrorBoundary>
      </Page>
  );
};

const mapStateToProps = (state: HomePageAppState) => ({
  topSectionDisplayName: getSectionDisplayNameByKey(SECTION_NAMES.TOP_SECTION)(state),
  topSectionArticles: getSectionArticlesByKey(SECTION_NAMES.TOP_SECTION)(state),
  cardsSectionDisplayName: getSectionDisplayNameByKey(SECTION_NAMES.CARDS_SECTION)(state),
  cardsSectionArticles: getSectionArticlesByKey(SECTION_NAMES.CARDS_SECTION)(state),
  cardsSectionLink: getSectionLinkByKey(SECTION_NAMES.CARDS_SECTION)(state),
  cardsSectionText: getSectionTextByKey(SECTION_NAMES.CARDS_SECTION)(state),
  contentSection1DisplayName: getSectionDisplayNameByKey(SECTION_NAMES.CONTENT_SECTION_1)(state),
  contentSection1Articles: getSectionArticlesByKey(SECTION_NAMES.CONTENT_SECTION_1)(state),
  contentSection1Link: getSectionLinkByKey(SECTION_NAMES.CONTENT_SECTION_1)(state),
  contentSection1Text: getSectionTextByKey(SECTION_NAMES.CONTENT_SECTION_1)(state),
  contentSection2DisplayName: getSectionDisplayNameByKey(SECTION_NAMES.CONTENT_SECTION_2)(state),
  contentSection2Articles: getSectionArticlesByKey(SECTION_NAMES.CONTENT_SECTION_2)(state),
  contentSection2Link: getSectionLinkByKey(SECTION_NAMES.CONTENT_SECTION_2)(state),
  contentSection2Text: getSectionTextByKey(SECTION_NAMES.CONTENT_SECTION_2)(state),
  contentSection3DisplayName: getSectionDisplayNameByKey(SECTION_NAMES.CONTENT_SECTION_3)(state),
  contentSection3Articles: getSectionArticlesByKey(SECTION_NAMES.CONTENT_SECTION_3)(state),
  contentSection3Link: getSectionLinkByKey(SECTION_NAMES.CONTENT_SECTION_3)(state),
  contentSection3Text: getSectionTextByKey(SECTION_NAMES.CONTENT_SECTION_3)(state),
  articles: getArticlesInTemplate(state),
  topAdId: getAdSlotId(SLOT_NAMES.TOP_AD)(state),
  oneOnOneId: getAdSlotId(SLOT_NAMES.ONE_ON_ONE)(state),
});

const HomePageContent: React.FunctionComponent<HomePageContentProps> = props => {
  const { topSectionDisplayName,
    topSectionArticles,
    cardsSectionDisplayName,
    cardsSectionArticles,
    cardsSectionLink,
    cardsSectionText,
    contentSection1DisplayName,
    contentSection1Articles,
    contentSection1Link,
    contentSection1Text,
    contentSection2DisplayName,
    contentSection2Articles,
    contentSection2Link,
    contentSection2Text,
    contentSection3DisplayName,
    contentSection3Articles,
    contentSection3Link,
    contentSection3Text,
    articles,
    topAdId,
    oneOnOneId } = props;
  return (
    <React.Fragment>
      <MetaTags articles={articles} />
      <HeadScripts />
      <Theme>
        <AppContent>
          <HeaderConnected />
          <MainWrapperConnected>
            <Ad id={topAdId} />
            <Section>
              <HeadlinesArticleList displayName={topSectionDisplayName} cardsProps={topSectionArticles} />
            </Section>

            <Section>
              <SectionHeader linkText={cardsSectionText} href={cardsSectionLink}>
                <h2>{cardsSectionDisplayName}</h2>
              </SectionHeader>
              <ScrollableArticleList cardsProps={cardsSectionArticles} />
            </Section>

            <Section>
              <SectionHeader linkText={contentSection1Text} href={contentSection1Link}>
                <h2>{contentSection1DisplayName}</h2>
              </SectionHeader>
              <OneThreeArticleList cardsProps={contentSection1Articles} />
            </Section>

            <Section>
              <SectionHeader linkText={contentSection2Text} href={contentSection2Link}>
                <h2>{contentSection2DisplayName}</h2>
              </SectionHeader>
              <OneThreeArticleList cardsProps={contentSection2Articles} />
            </Section>

            <Section>
              <SectionHeader linkText={contentSection3Text} href={contentSection3Link}>
                <h2>{contentSection3DisplayName}</h2>
              </SectionHeader>
              <OneThreeArticleList cardsProps={contentSection3Articles} />
            </Section>
            <Ad id={oneOnOneId} />
          </MainWrapperConnected>
          <FooterConnected />
        </AppContent>
      </Theme>
    </React.Fragment>
  );
};

export const HomePageContentConnected = connect(mapStateToProps)(HomePageContent);
